import { DefaultView } from "@redotech/effect-api-shared/models/support-ticket/default-view";

export type SupportNavEntry =
  | { kind: "defaultView"; id: DefaultView }
  | { kind: "view"; id: string }
  | { kind: "folder"; id: string };

interface OrderSupportNavEntriesInput {
  ordering: readonly { id: string; type: "view" | "folder" }[];
  topDefaultViewIds: readonly DefaultView[];
  bottomDefaultViewIds: readonly DefaultView[];
  folderedDefaultViewIds: ReadonlySet<string>;
  folders: readonly { id: string }[];
  folderlessViews: readonly { id: string; position: number }[];
}

// Fallback rank bands for entries absent from the saved ordering, spaced so
// array indices within a band never cross into the next band.
const RANK_FOLDER_BASE = 100_000;
const RANK_CUSTOM_BASE = 200_000;
const RANK_BOTTOM_DEFAULT_BASE = 300_000;

// Default views persist under type "view", matching how the handler saves them.
function refKey(type: "view" | "folder", id: string): string {
  return `${type}:${id}`;
}

// Flat ordered nav-entry refs for the support sidebar. Every top-level entry is
// reorderable; entries absent from the saved ordering fall back to canonical layout.
export function orderSupportNavEntries({
  ordering,
  topDefaultViewIds,
  bottomDefaultViewIds,
  folderedDefaultViewIds,
  folders,
  folderlessViews,
}: OrderSupportNavEntriesInput): SupportNavEntry[] {
  const savedIndex = new Map<string, number>();
  for (const [i, item] of ordering.entries()) {
    savedIndex.set(refKey(item.type, item.id), i);
  }

  const ranked: { saved: number; rank: number; ref: SupportNavEntry }[] = [];

  const folderlessDefaultViews = [
    ...topDefaultViewIds,
    ...bottomDefaultViewIds,
  ].filter((dv) => !folderedDefaultViewIds.has(dv));
  for (const dv of folderlessDefaultViews) {
    const topRank = topDefaultViewIds.indexOf(dv);
    const rank =
      topRank >= 0
        ? topRank
        : RANK_BOTTOM_DEFAULT_BASE + bottomDefaultViewIds.indexOf(dv);
    ranked.push({
      saved: savedIndex.get(refKey("view", dv)) ?? Infinity,
      rank,
      ref: { kind: "defaultView", id: dv },
    });
  }

  for (const [idx, folder] of folders.entries()) {
    ranked.push({
      saved: savedIndex.get(refKey("folder", folder.id)) ?? Infinity,
      rank: RANK_FOLDER_BASE + idx,
      ref: { kind: "folder", id: folder.id },
    });
  }

  for (const [idx, view] of [...folderlessViews]
    .sort((a, b) => a.position - b.position)
    .entries()) {
    ranked.push({
      saved: savedIndex.get(refKey("view", view.id)) ?? Infinity,
      rank: RANK_CUSTOM_BASE + idx,
      ref: { kind: "view", id: view.id },
    });
  }

  ranked.sort((a, b) =>
    a.saved !== b.saved ? a.saved - b.saved : a.rank - b.rank,
  );
  return ranked.map((r) => r.ref);
}
